import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';

import {
  CheckCircleOutlined,
  HistoryOutlined,
  QuestionCircleOutlined,
  SwapOutlined,
  WarningFilled,
} from '@ant-design/icons';

import CalendarWeekBar from 'components/cards/CalendarWeek';
import HistoryModule from 'components/cards/HistoryModule';
import ModuleCard from 'components/cards/ModuleCard';
import NextPeriodPrediction from 'components/cards/NextPeriodPrediction';
import CompareCycles from 'components/charts/CompareCycles';
import { CycleDotGraphExample } from 'components/charts/CycleDotGraph';
import PastCyclesLength from 'components/charts/PastCyclesLength';
import HorizontalTrackingCards from 'components/HorizontalTrackingCards';
import { Page, PageContent } from 'components/layout/page';
import PeriodQuickFillModal from 'components/modals/PeriodQuickFillModal';
import PageTopBar from 'components/navigation/PageTopBar';
import SymptomTrackingReminder from 'components/SymptomTrackingReminder';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import dayjs from 'dayjs';
import useCheckNotificationsPermissions from 'hooks/useCheckNotificationsPermissions';
import usePreferences from 'hooks/usePreferences';
import useReminderState from 'hooks/useReminderState';
import useSchedulePeriodNotifications from 'hooks/useSchedulePeriodNotifications';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import { cn } from 'lib/utils';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { usePushNotificationOptIn } from 'push/PushNotificationOptInContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Platform from 'utils/Platform';
import ScrollUtil from 'utils/ScrollUtil';
import SymptomTrackingSurveyModal from './SymptomTrackingSurvey';

// The "?" icons that open the help dialogs
const HELPER_ICON_CLASS = 'mt-2 cursor-pointer text-subtle-foreground';

// Legend dots in the cycle-length help dialog. Same colours as `CycleDotGraph`.
const CYCLE_DOT_EXAMPLE_CLASS = 'mx-1 inline-block size-2 rounded-full';

export default function SymptomTracking() {
  // Hooks
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { doesUserHavePushNotifications } = useCheckNotificationsPermissions();

  // period tracking preferences
  const { isPeriodTrackingEnabled, setShouldAskForPeriod } = useShouldAskForPeriod();

  // reminder state (managed via both preferences and Redux store)
  const { isPeriodReminderEnabled, areDailyNotificationsEnabled, setPeriodReminderEnabled } = useReminderState();

  const isAnyNotificationEnabled = areDailyNotificationsEnabled || isPeriodReminderEnabled;
  const { schedulePeriodReminder } = useSchedulePeriodNotifications();

  const {
    set: setDidWeAlreadySuggestPeriodReminder,
    value: didWeAlreadySuggestPeriodReminder,
    loading: loadingDidWeAlreadySuggestPeriodReminder,
  } = usePreferences(Keys.TRACKING_DID_WE_ALREADY_SUGGEST_PERIOD_REMINDER);

  const { requestOptIn, enableNotifications } = usePushNotificationOptIn();

  const areNotificationsBlocked = isAnyNotificationEnabled && !doesUserHavePushNotifications;

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [showPeriodReminderSuggestion, setShowPeriodReminderSuggestion] = useState(false);

  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);

  // Helper modal states
  const [showCycleLengthHelp, setShowCycleLengthHelp] = useState(false);
  const [showCompareCyclesHelp, setShowCompareCyclesHelp] = useState(false);

  // period quick fill modal state
  const [isQuickFillModalOpen, setIsQuickFillModalOpen] = useState(false);

  // history / compare toggle state
  const [selectedTab, setSelectedTab] = useState<'history' | 'compare'>('history');

  const shouldShowPeriodReminderSuggestion =
    Platform.isNative() &&
    ((showPeriodReminderSuggestion &&
      !loadingDidWeAlreadySuggestPeriodReminder &&
      !(didWeAlreadySuggestPeriodReminder === true)) ||
      areNotificationsBlocked);

  const [periodReminderPage, setPeriodReminderPage] = useState<
    'ask' | 'confirmNo' | 'confirmYes' | 'notificationsDisabled'
  >('ask');

  const { refresh, hasTrackingBeenUsed, probableNextPeriodDates, hasTrackingForToday } = useSymptomTrackingData();

  const enoughDataForHistory = hasTrackingBeenUsed;

  useEffect(() => {
    // sometimes in functional tests, I think useRequest considers two very close requests as the same
    // and does not refresh the data when this page mounts. This fixes it
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (areNotificationsBlocked) {
      setPeriodReminderPage('notificationsDisabled');
    }
  }, [areNotificationsBlocked]);

  useEffect(() => {
    if (isPeriodTrackingEnabled && probableNextPeriodDates && !showPeriodReminderSuggestion) {
      setShowPeriodReminderSuggestion(true);
      setPeriodReminderPage('ask');
    }
  }, [isPeriodTrackingEnabled, probableNextPeriodDates, showPeriodReminderSuggestion]);

  const onFillCycles = () => {
    // open new quick fill modal with infinite calendar
    setIsQuickFillModalOpen(true);
    posthog.capture('tracking.period_quickfill_started');
  };

  const wasQuickFillOpenRef = useRef(false);
  useEffect(() => {
    if (wasQuickFillOpenRef.current && !isQuickFillModalOpen) {
      requestOptIn('first_tracking');
    }
    wasQuickFillOpenRef.current = isQuickFillModalOpen;
  }, [isQuickFillModalOpen, requestOptIn]);

  const onValidateFillingFirstPeriods = () => {
    setIsCalendarOpen(false);
    ScrollUtil.scrollToTop(containerRef.current);
    posthog.capture('tracking.period_quickfill_validated');
  };

  const enablePeriodReminder = useCallback(() => {
    setPeriodReminderEnabled(true);
    setPeriodReminderPage('confirmYes');
    if (probableNextPeriodDates) {
      schedulePeriodReminder(probableNextPeriodDates.start, areDailyNotificationsEnabled);
    }
    posthog.capture('tracking.period_reminder.enabled', { source: 'suggester' });
  }, [setPeriodReminderEnabled, probableNextPeriodDates, schedulePeriodReminder, areDailyNotificationsEnabled]);

  const refusePeriodReminder = () => {
    setPeriodReminderPage('confirmNo');
    posthog.capture('tracking.period_reminder.refused');
  };

  useEffect(() => {
    // when coming back from notification settings, if notifications have been enabled,
    // enable the reminder and switch to the confirmation page
    if (doesUserHavePushNotifications && periodReminderPage === 'notificationsDisabled') {
      enablePeriodReminder();
    }
  }, [doesUserHavePushNotifications, periodReminderPage, enablePeriodReminder]);

  const setIsCalendarOpenWithScrollHandler = (newIsCalendarOpen: boolean) => {
    setIsCalendarOpen(newIsCalendarOpen);
    if (newIsCalendarOpen) {
      containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const ErrorFallback = (props: FallbackProps) => {
    void props;
    return (
      <div className="flex h-full items-center justify-center px-8 text-center text-chart-severity-bad">
        <p>{t('symptomTracking.error.title')}</p>
      </div>
    );
  };

  return (
    <Page>
      <PageTopBar title={t('symptomTracking.title')} />
      <PageContent className="items-center gap-8 px-3 pt-2 pb-8" ref={containerRef}>
        <CalendarWeekBar
          isCalendarOpen={isCalendarOpen}
          setIsCalendarOpen={setIsCalendarOpenWithScrollHandler}
          onFillCycles={onFillCycles}
        />
        {/* Quick fill modal for cycles */}
        <PeriodQuickFillModal
          open={isQuickFillModalOpen}
          onClose={() => setIsQuickFillModalOpen(false)}
          onValidate={() => {
            setIsQuickFillModalOpen(false);
            onValidateFillingFirstPeriods();
          }}
        />

        {/* Next period prediction and reminder */}
        {isPeriodTrackingEnabled && (
          <div className="relative flex w-full flex-col items-center gap-6">
            <NextPeriodPrediction />
            <div className="absolute right-0 bottom-0">
              <SymptomTrackingReminder mode="iconOnly" />
            </div>
          </div>
        )}

        {/* Standalone reminder when period tracking is disabled */}
        {!isPeriodTrackingEnabled && (
          <div className="flex w-full justify-center">
            <SymptomTrackingReminder mode="full" />
          </div>
        )}

        {/* Period reminder suggestion */}
        {shouldShowPeriodReminderSuggestion && (
          <Alert variant={periodReminderPage === 'notificationsDisabled' ? 'warning' : 'success'}>
            <AlertDescription className="justify-items-center text-center">
              <div className="my-3 flex flex-col items-center gap-3 text-center">
                {periodReminderPage === 'ask' && (
                  <>
                    <p>{t('symptomTracking.periodReminder.question')}</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <Button variant="ghost" onClick={refusePeriodReminder}>
                        {t('symptomTracking.periodReminder.later')}
                      </Button>
                      <Button
                        onClick={() => {
                          if (doesUserHavePushNotifications) {
                            enablePeriodReminder();
                          } else {
                            setPeriodReminderPage('notificationsDisabled');
                          }
                        }}
                      >
                        {t('symptomTracking.periodReminder.yes')}
                      </Button>
                    </div>
                  </>
                )}
                {periodReminderPage === 'confirmNo' && (
                  <>
                    <p>{t('symptomTracking.periodReminder.laterExplanation')}</p>
                    <div>
                      <Button
                        onClick={() => {
                          setShowPeriodReminderSuggestion(false);
                          setDidWeAlreadySuggestPeriodReminder('true');
                        }}
                      >
                        {t('symptomTracking.periodReminder.ok')}
                      </Button>
                    </div>
                  </>
                )}
                {periodReminderPage === 'confirmYes' && (
                  <>
                    <p>
                      <CheckCircleOutlined className="mr-2 text-success-text" />{' '}
                      {t('symptomTracking.periodReminder.enabled')}
                    </p>
                    <div>
                      <Button
                        onClick={() => {
                          setShowPeriodReminderSuggestion(false);
                          setDidWeAlreadySuggestPeriodReminder('true');
                        }}
                      >
                        {t('symptomTracking.periodReminder.ok')}
                      </Button>
                    </div>
                  </>
                )}
                {periodReminderPage === 'notificationsDisabled' && (
                  <>
                    <p>{t('symptomTracking.reminder.enableNotifications')}</p>
                    <div>
                      <Button
                        onClick={() => {
                          posthog.capture('notification_suggester_clicked');
                          enableNotifications();
                        }}
                      >
                        {t('symptomTracking.periodReminder.enable')}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Horizontal cards */}
        <HorizontalTrackingCards
          displayTomorrow={!areDailyNotificationsEnabled}
          onOpenCalendar={() => setIsCalendarOpenWithScrollHandler(true)}
        />

        {/* Past cycles length */}
        {isPeriodTrackingEnabled && (
          <ModuleCard
            title={t('symptomTracking.modules.cycleLengthTitle')}
            topRightContent={
              <QuestionCircleOutlined
                onClick={() => {
                  setShowCycleLengthHelp(true);
                  posthog.capture('tracking.cycle_length_help_opened');
                }}
                className={HELPER_ICON_CLASS}
              />
            }
          >
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onError={(error, errorInfo) => console.error('Error in PastCyclesLength', error, errorInfo)}
            >
              <PastCyclesLength onFillCycles={onFillCycles} />
            </ErrorBoundary>
          </ModuleCard>
        )}

        {/* History / compare toggle */}
        {enoughDataForHistory && isPeriodTrackingEnabled && (
          <div className="mt-6">
            <ToggleGroup
              type="single"
              variant="default"
              size="lg"
              value={selectedTab}
              onValueChange={value => {
                // keep it non-deselectable: clicking the selected item again yields ''
                if (value !== 'history' && value !== 'compare') return;
                setSelectedTab(value);
                posthog.capture('tracking.history_graph_switcher_changed', {
                  selectedTab: value,
                });
              }}
              className="bg-card shadow-xs"
            >
              {/* The checked item asks specifically for paon, not the app-wide accent fill */}
              <ToggleGroupItem
                value="history"
                className="data-[state=on]:bg-inverse data-[state=on]:text-inverse-foreground"
              >
                <HistoryOutlined />
                {t('symptomTracking.modules.historyTitle')}
              </ToggleGroupItem>
              <ToggleGroupItem
                value="compare"
                className="data-[state=on]:bg-inverse data-[state=on]:text-inverse-foreground"
              >
                <SwapOutlined />
                {t('symptomTracking.modules.perCycle')}
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        )}

        {/* History / cycles comparison module */}
        {selectedTab === 'history' ? (
          <HistoryModule enoughDataForHistory={!!enoughDataForHistory} />
        ) : (
          enoughDataForHistory &&
          isPeriodTrackingEnabled && (
            <ModuleCard
              title={t('symptomTracking.modules.compareCyclesTitle')}
              topRightContent={
                <QuestionCircleOutlined
                  onClick={() => {
                    setShowCompareCyclesHelp(true);
                    posthog.capture('tracking.compare_cycles_help_opened');
                  }}
                  className={HELPER_ICON_CLASS}
                />
              }
            >
              <ErrorBoundary
                FallbackComponent={ErrorFallback}
                onError={(error, errorInfo) => console.error('Error in CompareCycles', error, errorInfo)}
              >
                <CompareCycles />
              </ErrorBoundary>
            </ModuleCard>
          )
        )}

        {/* No periods module */}
        {!isPeriodTrackingEnabled && (
          <ModuleCard title={t('symptomTracking.graphs.menstruation')}>
            <div className="flex items-center justify-between gap-3">
              <p className="m-0">{t('symptomTracking.tracking.disabled')}</p>
              <Button
                variant="link"
                onClick={() => {
                  setShouldAskForPeriod(true);
                  posthog.capture('tracking.period_tracking_reactivated');
                }}
              >
                {t('symptomTracking.tracking.reactivate')}
              </Button>
            </div>
          </ModuleCard>
        )}
        {!hasTrackingForToday && <div className="h-16 min-h-16" />}
      </PageContent>
      {!hasTrackingForToday && (
        <div className="absolute bottom-0 z-10 w-full bg-linear-to-t from-background via-background via-50% to-background/0 p-8">
          <Button
            size="xl"
            className="w-full"
            onClick={() => {
              setIsSurveyModalOpen(true);
              posthog.capture('tracking.survey.started', {
                source: 'daily_button',
              });
            }}
          >
            {t('symptomTracking.actions.fillDailySurvey')}
          </Button>
        </div>
      )}

      {/* Survey modal */}
      <SymptomTrackingSurveyModal
        open={isSurveyModalOpen}
        key={dayjs().format('YYYY-MM-DD')}
        onClose={() => {
          setIsSurveyModalOpen(false);
          refresh();
        }}
        date={dayjs()}
      />

      {/* Helper Modals */}
      <Dialog open={showCycleLengthHelp} onOpenChange={open => !open && setShowCycleLengthHelp(false)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t('symptomTracking.modules.cycleLengthHelp.title')}</DialogTitle>
          </DialogHeader>
          {/* The explainer reads as a sequence of short blocks: room between each */}
          <div className="flex flex-col gap-4">
            <p>
              <Trans i18nKey="symptomTracking.modules.cycleLengthHelp.description" components={{ br: <br /> }} />
            </p>
            <h4 className="mt-2">{t('symptomTracking.modules.cycleLengthHelp.howToRead')}</h4>
            <div className="flex justify-center">
              <CycleDotGraphExample />
            </div>
            <p>
              <Trans
                i18nKey="symptomTracking.modules.cycleLengthHelp.description2"
                components={{
                  redDot: <span className={cn(CYCLE_DOT_EXAMPLE_CLASS, 'bg-chart-severity-very-bad')} />,
                  midBlueDot: <span className={cn(CYCLE_DOT_EXAMPLE_CLASS, 'bg-info')} />,
                  darkBlueDot: <span className={cn(CYCLE_DOT_EXAMPLE_CLASS, 'bg-inverse')} />,
                }}
              />
            </p>
            <Alert variant="warning">
              <AlertDescription className="flex flex-row items-start gap-2">
                <WarningFilled className="text-chart-severity-bad" />
                <span>{t('symptomTracking.cycles.fertilityWarning')}</span>
              </AlertDescription>
            </Alert>
            <p>
              <Trans
                i18nKey="symptomTracking.modules.cycleLengthHelp.description3"
                components={{
                  lightBlueDot: <span className={cn(CYCLE_DOT_EXAMPLE_CLASS, 'border-2 border-info')} />,
                  darkBlueDot: <span className={cn(CYCLE_DOT_EXAMPLE_CLASS, 'border-2 border-inverse')} />,
                }}
              />
            </p>
            <div className="flex justify-center">
              <CycleDotGraphExample showInProgress={true} />
            </div>
            <p>
              <Trans
                i18nKey="symptomTracking.modules.cycleLengthHelp.description4"
                components={{
                  todayDot: (
                    <span
                      className={cn(
                        CYCLE_DOT_EXAMPLE_CLASS,
                        'inline-flex size-3.5 items-center justify-center border-2 border-muted-foreground align-middle',
                      )}
                    >
                      <span className="size-2 rounded-full bg-subtle-foreground" />
                    </span>
                  ),
                }}
              />
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCompareCyclesHelp} onOpenChange={open => !open && setShowCompareCyclesHelp(false)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t('symptomTracking.modules.compareCyclesHelp.title')}</DialogTitle>
          </DialogHeader>
          <p>
            <Trans i18nKey="symptomTracking.modules.compareCyclesHelp.description" components={{ br: <br /> }} />
          </p>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
